import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import CONFIG from '@/config/env';

export default function CliCallback() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    const processCallback = async () => {
      try {
        const sessionId = searchParams.get('session');
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!sessionId) {
          setStatus('Error: Missing session ID');
          toast.error('Invalid callback - missing session ID');
          return;
        }

        if (!code) {
          setStatus('Error: Authentication cancelled');
          toast.error('Authentication was cancelled');
          return;
        }

        // Exchange code for CLI token directly (no user token needed)
        setStatus('Exchanging authorization code for CLI token...');
        const tokenResponse = await fetch(`${CONFIG.API_BASE}/api/cli-tokens/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            state,
            session_id: sessionId,
          }),
        });

        if (!tokenResponse.ok) {
          const error = await tokenResponse.text();
          throw new Error(`Token generation failed: ${error}`);
        }

        const tokenData = await tokenResponse.json() as { token?: string; workspace_id?: string; workspace_name?: string };

        if (!tokenData.token) {
          throw new Error('No token received from server');
        }

        // Store token in session storage for CLI to poll
        setStatus('Storing authentication token...');
        const storeResponse = await fetch(`${CONFIG.API_BASE}/api/cli/token/${sessionId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: tokenData.token,
            workspace_id: tokenData.workspace_id,
            workspace_name: tokenData.workspace_name,
          }),
        });

        if (!storeResponse.ok) {
          throw new Error('Failed to store token for CLI');
        }

        setStatus('✓ Authentication successful! You can close this window.');
        toast.success('CLI authentication successful!');

        // Close window after 2 seconds
        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        setStatus(`Error: ${message}`);
        toast.error(`Authentication failed: ${message}`);
      }
    };

    processCallback();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 border border-white/20">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
        <h1 className="text-2xl font-bold text-white">CLI Authentication</h1>
        <p className="text-white/60">{status}</p>
      </div>
    </div>
  );
}
